import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useEmpresaActiva } from '../../context/EmpresaContableContext';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem,
  CircularProgress, Alert, Chip, Divider
} from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const API = '/api/contabilidad';

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtK(v) {
  const n = Math.abs(parseFloat(v) || 0);
  if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n/1000).toFixed(0)}K`;
  return n.toFixed(0);
}
function fmtMes(p) {
  if (!p || p.length !== 6) return p;
  const mn = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${mn[parseInt(p.substring(4,6))-1]}-${p.substring(2,4)}`;
}

// ── KPI Card profesional con gradiente ──
function KpiCard({ label, value, gradient, icon, sub, negativeIsRed, showAbsolute }) {
  const isLoss = negativeIsRed && value < 0;
  const displayGradient = isLoss
    ? 'linear-gradient(135deg, #b71c1c 0%, #e53935 100%)'
    : gradient;
  return (
    <Paper sx={{
      borderRadius: 3, overflow: 'hidden', height: '100%',
      background: displayGradient,
      boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
      transition: 'transform .2s, box-shadow .2s',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 32px rgba(0,0,0,0.22)' }
    }}>
      <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.22)', borderRadius: '50%',
            width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <Box sx={{ color: 'white', display: 'flex', fontSize: 22 }}>{icon}</Box>
          </Box>
          {sub && (
            <Chip label={sub} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontSize: '0.68rem', fontWeight: 'bold', height: 22 }} />
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, color: 'white', lineHeight: 1.2, mb: 0.5 }}>
            S/ {fmt(showAbsolute ? Math.abs(value) : value)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.3 }}>
            {label}
          </Typography>
          {isLoss && (
            <Chip label="Pérdida neta" size="small"
              sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.22)', color: 'white', fontSize: '0.68rem' }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
}

// ── Tooltip personalizado recharts ──
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', minWidth: 160 }}>
      <Typography variant="caption" fontWeight="bold" color="text.secondary" mb={0.5} display="block">{label}</Typography>
      {payload.map((p, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, my: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
          <Typography variant="caption">{p.name}:</Typography>
          <Typography variant="caption" fontWeight="bold">S/ {fmt(p.value)}</Typography>
        </Box>
      ))}
    </Paper>
  );
};

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
      <Typography variant="caption" fontWeight="bold" color="text.secondary">{p.name}</Typography><br />
      <Typography variant="caption" fontWeight="bold">S/ {fmt(p.value)}</Typography>
    </Paper>
  );
};

export default function DashboardContable() {
  const { empresaActivaId, setEmpresaActiva } = useEmpresaActiva();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(empresaActivaId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setData(null); return; }
    setLoading(true); setError('');
    axios.get(`${API}/dashboard?empresa_id=${empresaId}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error al cargar dashboard'))
      .finally(() => setLoading(false));
  }, [empresaId]);

  const evolChart = (data?.evolucion || []).map(e => ({
    mes: fmtMes(e.periodo),
    Ingresos: parseFloat(e.ingresos) || 0,
    Gastos: parseFloat(e.gastos) || 0,
    Resultado: (parseFloat(e.ingresos) || 0) - (parseFloat(e.gastos) || 0),
  }));

  const margenPct = data?.ingresos > 0
    ? ((data.resultado / data.ingresos) * 100).toFixed(1)
    : null;

  // Datos donut: estructura de balance disponible
  const donutData = [
    { name: 'Caja y Bancos', value: Math.abs(parseFloat(data?.caja_bancos) || 0), color: '#1976d2' },
    { name: 'Cuentas x Cobrar', value: Math.abs(parseFloat(data?.cxc) || 0), color: '#00897b' },
    { name: 'Cuentas x Pagar', value: Math.abs(parseFloat(data?.cxp) || 0), color: '#f57c00' },
  ].filter(d => d.value > 0);

  // Top cuentas horizontal bar
  const topBarData = (data?.top_ingresos || []).map(c => ({
    nombre: c.codigo ? `${c.codigo}` : c.nombre,
    full: c.nombre,
    Saldo: parseFloat(c.saldo) || 0,
  }));

  const periodosMap = {};
  (data?.periodos_stats || []).forEach(p => { periodosMap[p.estado] = parseInt(p.cnt); });

  const KPIS = data ? [
    { label: 'Caja y Bancos', value: data.caja_bancos, gradient: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)', icon: <AccountBalanceIcon fontSize="inherit" />, sub: 'Acumulado' },
    { label: 'Cuentas por Cobrar', value: data.cxc, gradient: 'linear-gradient(135deg, #00695c 0%, #26a69a 100%)', icon: <PeopleAltIcon fontSize="inherit" />, sub: 'Cta. 12x' },
    { label: 'Cuentas por Pagar', value: data.cxp, gradient: 'linear-gradient(135deg, #e65100 0%, #ffa726 100%)', icon: <ShoppingCartIcon fontSize="inherit" />, sub: 'Cta. 42x' },
    { label: `Ingresos — ${data.periodo_actual || ''}`, value: data.ingresos, gradient: 'linear-gradient(135deg, #1b5e20 0%, #43a047 100%)', icon: <TrendingUpIcon fontSize="inherit" />, sub: 'Cta. 7x' },
    { label: `Gastos — ${data.periodo_actual || ''}`, value: data.gastos, gradient: 'linear-gradient(135deg, #7f0000 0%, #ef5350 100%)', icon: <TrendingDownIcon fontSize="inherit" />, sub: 'Cta. 6x', showAbsolute: true },
    { label: `Resultado — ${data.periodo_actual || ''}`, value: data.resultado, gradient: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)', icon: <ShowChartIcon fontSize="inherit" />, sub: margenPct ? `Margen ${margenPct}%` : 'Período', negativeIsRed: true, showAbsolute: true },
  ] : [];

  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto', mt: 1, px: { xs: 1, sm: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, p: 0.8, display: 'flex', color: 'white' }}>
          <DashboardIcon />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold" lineHeight={1.2}>Dashboard Contable</Typography>
          <Typography variant="caption" color="text.secondary">Indicadores financieros en tiempo real</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Selector empresa ── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#f8faff', border: '1px solid #e3eaf5' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId}
              onChange={e => { setEmpresaId(e.target.value); setEmpresaActiva(e.target.value); }} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          {data?.periodo_actual && (
            <Grid item xs={12} sm="auto">
              <Chip icon={<CalendarTodayIcon />}
                label={`Período activo: ${fmtMes(data.periodo_actual)}`}
                color="primary" variant="filled" sx={{ fontWeight: 'bold' }} />
            </Grid>
          )}
          {margenPct && (
            <Grid item xs={12} sm="auto">
              <Chip
                label={`Margen neto: ${margenPct}%`}
                sx={{
                  bgcolor: parseFloat(margenPct) >= 0 ? '#e8f5e9' : '#ffebee',
                  color: parseFloat(margenPct) >= 0 ? '#1b5e20' : '#b71c1c',
                  fontWeight: 'bold', border: '1px solid', borderColor: parseFloat(margenPct) >= 0 ? '#a5d6a7' : '#ef9a9a'
                }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 6 }} />}

      {!loading && !empresaId && (
        <Paper sx={{ p: 8, borderRadius: 3, textAlign: 'center', bgcolor: '#f8faff', border: '1px dashed #c5d5ea' }}>
          <Box sx={{ bgcolor: '#e3eaf5', borderRadius: '50%', width: 80, height: 80, mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DashboardIcon sx={{ fontSize: 40, color: '#90a4ae' }} />
          </Box>
          <Typography variant="h6" color="text.secondary" fontWeight="medium">
            Selecciona una empresa para ver el dashboard
          </Typography>
          <Typography variant="body2" color="text.disabled" mt={0.5}>
            Los indicadores financieros aparecerán aquí
          </Typography>
        </Paper>
      )}

      {!loading && data && (
        <>
          {/* ── KPI Cards — 3 + 3 ── */}
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
            {KPIS.map((k, i) => (
              <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
                <KpiCard {...k} />
              </Grid>
            ))}
          </Grid>

          {/* ── Fila 2: Evolución (izq) + Donut + Asientos (der, apilados) ── */}
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">

            {/* Evolución financiera */}
            <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
              <Paper sx={{ p: 3, borderRadius: 3, width: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                <Typography variant="h6" fontWeight="bold" mb={0.3}>Evolución Financiera</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2.5}>
                  Ingresos vs Gastos — últimos 6 meses
                </Typography>
                {evolChart.length > 0 ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <ResponsiveContainer width="100%" minWidth={380} height={260}>
                      <AreaChart data={evolChart} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="gradIng" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#43a047" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#43a047" stopOpacity={0.03} />
                          </linearGradient>
                          <linearGradient id="gradGas" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef5350" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#ef5350" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="mes" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={v => `S/${fmtK(v)}`} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="Ingresos" stroke="#43a047" strokeWidth={2.5}
                          fill="url(#gradIng)" dot={{ r: 4, fill: '#43a047', strokeWidth: 0 }}
                          activeDot={{ r: 6, strokeWidth: 0 }} />
                        <Area type="monotone" dataKey="Gastos" stroke="#ef5350" strokeWidth={2.5}
                          fill="url(#gradGas)" dot={{ r: 4, fill: '#ef5350', strokeWidth: 0 }}
                          activeDot={{ r: 6, strokeWidth: 0 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.disabled">Sin movimientos en los últimos 6 meses</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Columna derecha: Donut + Asientos apilados */}
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>

                {/* Donut estructura financiera */}
                {donutData.length > 0 && (
                  <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                    <Typography variant="h6" fontWeight="bold" mb={0.3}>Estructura Financiera</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Balance de activos y pasivos
                    </Typography>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={donutData} cx="50%" cy="50%"
                          innerRadius={44} outerRadius={68}
                          paddingAngle={4} dataKey="value" startAngle={90} endAngle={-270}>
                          {donutData.map((d, i) => (
                            <Cell key={i} fill={d.color} stroke="none" />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box>
                      {donutData.map((d, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary">{d.name}</Typography>
                          </Box>
                          <Typography variant="caption" fontWeight="bold">S/ {fmt(d.value)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Asientos del período */}
                <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <ReceiptLongIcon color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight="bold">
                      Asientos — {fmtMes(data.periodo_actual) || '—'}
                    </Typography>
                  </Box>
                  <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                    <Grid item xs={6}>
                      <Paper sx={{
                        p: 1.5, textAlign: 'center', borderRadius: 2,
                        background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
                        border: '1px solid #c8e6c9'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.3 }}>
                          <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 16 }} />
                          <Typography variant="h5" fontWeight="bold" color="success.dark">
                            {data.asientos?.asentados || 0}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="success.dark" fontWeight="medium">Activos</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{
                        p: 1.5, textAlign: 'center', borderRadius: 2,
                        background: 'linear-gradient(135deg, #fce4ec 0%, #fff8e1 100%)',
                        border: '1px solid #f8bbd9'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.3 }}>
                          <CancelIcon sx={{ color: '#c62828', fontSize: 16 }} />
                          <Typography variant="h5" fontWeight="bold" color="error.dark">
                            {data.asientos?.borradores || 0}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="error.dark" fontWeight="medium">Anulados</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                    <CalendarTodayIcon color="action" sx={{ fontSize: 16 }} />
                    <Typography variant="caption" fontWeight="bold" color="text.secondary">Períodos contables</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                    {(data.periodos_stats || []).map(p => (
                      <Chip key={p.estado} label={`${p.estado}: ${p.cnt}`} size="small"
                        sx={{
                          fontWeight: 'bold', fontSize: '0.7rem',
                          bgcolor: p.estado === 'ABIERTO' ? '#e8f5e9' : p.estado === 'CERRADO' ? '#e3f2fd' : '#fff3e0',
                          color: p.estado === 'ABIERTO' ? '#1b5e20' : p.estado === 'CERRADO' ? '#0d47a1' : '#e65100',
                          border: '1px solid',
                          borderColor: p.estado === 'ABIERTO' ? '#a5d6a7' : p.estado === 'CERRADO' ? '#90caf9' : '#ffcc80',
                        }}
                      />
                    ))}
                  </Box>
                </Paper>

              </Box>
            </Grid>
          </Grid>

          {/* ── Fila 3: Top Cuentas (izq) + Resultado/Margen (der) ── */}
          <Grid container spacing={2}>
            {topBarData.length > 0 && (
              <Grid item xs={12} md={data.ingresos > 0 ? 8 : 12}>
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                  <Typography variant="h6" fontWeight="bold" mb={0.3}>Top Cuentas de Ingresos</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Período {data.periodo_actual}
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <ResponsiveContainer width="100%" minWidth={280} height={210}>
                      <BarChart data={topBarData} layout="vertical"
                        margin={{ top: 0, right: 65, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                        <XAxis type="number" tickFormatter={v => `S/${fmtK(v)}`}
                          tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="nombre"
                          tick={{ fontSize: 11, fontFamily: 'monospace' }} width={52}
                          axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="Saldo" fill="#43a047" radius={[0, 6, 6, 0]}
                          label={{ position: 'right', formatter: v => `S/${fmtK(v)}`, fontSize: 11, fill: '#388e3c' }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  {(data?.top_ingresos || []).map((c, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{c.nombre}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}

            {data.ingresos > 0 && (
              <Grid item xs={12} md={topBarData.length > 0 ? 4 : 12} sx={{ display: 'flex' }}>
                <Paper sx={{
                  p: 3, borderRadius: 3, width: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  background: data.resultado >= 0
                    ? 'linear-gradient(160deg, #f1f8e9 0%, #e8f5e9 60%, #c8e6c9 100%)'
                    : 'linear-gradient(160deg, #fce4ec 0%, #ffebee 60%, #f8bbd9 100%)',
                  border: '1px solid',
                  borderColor: data.resultado >= 0 ? '#c8e6c9' : '#f8bbd9'
                }}>
                  <Typography variant="h6" fontWeight="bold"
                    color={data.resultado >= 0 ? 'success.dark' : 'error.dark'} mb={2}>
                    {data.resultado >= 0 ? 'Utilidad del período' : 'Pérdida del período'}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Ingresos</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.dark">S/ {fmt(data.ingresos)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Gastos</Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">S/ {fmt(data.gastos)}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="bold" color="text.primary">Resultado</Typography>
                        <Typography variant="body2" fontWeight="bold"
                          color={data.resultado >= 0 ? 'success.dark' : 'error.dark'}>
                          S/ {fmt(Math.abs(data.resultado))}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.8}>
                    Margen neto: {margenPct}%
                  </Typography>
                  <Box sx={{ bgcolor: 'rgba(0,0,0,0.07)', borderRadius: 2, height: 10, overflow: 'hidden' }}>
                    <Box sx={{
                      width: `${Math.min(Math.abs(parseFloat(margenPct) || 0), 100)}%`,
                      height: '100%',
                      bgcolor: data.resultado >= 0 ? '#43a047' : '#ef5350',
                      borderRadius: 2,
                      transition: 'width .6s ease'
                    }} />
                  </Box>
                  <Typography variant="h3" fontWeight="bold" textAlign="center" mt={3}
                    color={data.resultado >= 0 ? 'success.dark' : 'error.dark'}>
                    {margenPct}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                    de los ingresos es {data.resultado >= 0 ? 'utilidad' : 'pérdida'}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
}
